/**
 * `useEntityDetails` — thin React Query hook around the existing
 * `api.graph.getNode` endpoint.
 *
 * Per design D5: the same hook is used across Knowledge Graph, Timeline, and
 * Time Axis so that opening the same node on two views reuses the cached
 * response. Edges have no per-edge endpoint today, so this hook returns the
 * in-memory selection unchanged for edge selections.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { SelectedEdge, SelectedNode } from '@/components/neo4j/cytoscape/types'
import {
  type MergedEntityRedirect,
  parseMergedRedirect,
  retryUnlessGone,
} from '@/features/entity-resolution/merged-redirect'
import { api } from '@/services/api'
import type { Person } from '@/services/api/graph'

/**
 * Cache key for one node's detail payload. Exported so invalidation/eviction
 * sites share the hook's cache contract instead of restating the literal.
 */
export const entityDetailsQueryKey = (caseId: string, nodeId: string | null) =>
  ['graph', caseId, 'node', nodeId] as const

interface UseEntityDetailsArgs {
  caseId: string
  selectedNode: SelectedNode | null
  selectedEdge: SelectedEdge | null
}

interface UseEntityDetailsResult {
  fullNodeDetails: Person | null
  isLoading: boolean
  error: Error | null
  /** Set when the node is gone because entity resolution folded it into another. */
  mergedInto: MergedEntityRedirect | null
  refetch: () => void
}

export function useEntityDetails({
  caseId,
  selectedNode,
  selectedEdge,
}: UseEntityDetailsArgs): UseEntityDetailsResult {
  const nodeId = selectedNode?.id ?? null
  const enabled = Boolean(nodeId && !selectedEdge && caseId)

  const query = useQuery<Person>({
    queryKey: entityDetailsQueryKey(caseId, nodeId),
    queryFn: () => api.graph.getNode(caseId, nodeId as string),
    enabled,
    // A missing (404) or merged-away (410) node is a permanent answer — retrying
    // only delays the explanation the UI is about to render.
    retry: retryUnlessGone,
  })

  const error = (query.error as Error | null) ?? null
  const mergedInto = useMemo(() => parseMergedRedirect(error), [error])

  return {
    fullNodeDetails: query.data ?? null,
    isLoading: enabled && query.isLoading,
    error,
    mergedInto,
    refetch: () => {
      void query.refetch()
    },
  }
}
